<template>
  <v-card>
    <v-card-title class="d-flex flex-column align-baseline">
      <div>
        <h3 class="mb-0 card-title-information">Informações</h3>
      </div>
      <p class="mb-0 subtitle pt-1" style="word-break: break-word">
        {{
          isCreate
            ? 'Produtos criados aqui precisam estar cadastrados no seu ERP/PDV para serem utilizados efetivamente numa campanha de ofertas. '
            : 'Edições feitas serão aplicadas em todos os lugares que consomem produtos, como por exemplo seu frente de caixa e aplicativo.'
        }}
      </p>
    </v-card-title>
    <v-row class="px-8">
      <v-divider></v-divider>
    </v-row>
    <v-card-text class="card-information px-10">
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-col cols="3">
              <mf-upload-image
                v-if="productForms.images.length"
                v-model="localImage"
                file-name="product"
                :loading="isLoading"
                :disabled="isLoading || !userHasPermission"
                label-formats-accepted="PNG, JPG, JPEG ou WEBP."
                :validation="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
                @snackbar-error="showSnackbarError"
                @upload="saveImage"
              />
              <mf-icon v-else color="cinza-0" class="icon-align"> image </mf-icon>
              <mf-toggle v-if="productForms.isMedicine" v-model="productForms.isMedicine" class="pb-n10" label="Mostrar imagem" color="primary" hide-details />
              <p v-if="productForms.isMedicine" class="pt-2 texto-corrido">Selecione essa opção para mostrar a imagem do medicamento no ecommerce</p>
            </v-col>
            <v-col cols="9">
              <v-row>
                <v-col cols="5">
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="productForms.name"
                        label="Nome"
                        class="pb-2"
                        outlined
                        color="#C3C8CD"
                        height="44px"
                        :disabled="!userHasPermission"
                        :error="validation.product.name.$error"
                        :error-messages="nameError"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <v-row>
                    <v-col cols="10">
                      <v-text-field
                        v-model="productForms.legacySku"
                        class="pb-2"
                        label="SKU"
                        outlined
                        :disabled="disableSkuInput"
                        color="#C3C8CD"
                        height="44px"
                        type="number"
                        min="0"
                        max="14"
                        :hint="!disableSkuInput && !isCreate ? 'Ao inserir o SKU, não será possível realizar edições posteriormente.' : ''"
                        :error="validation.product.legacySku && validation.product.legacySku.$error"
                        :error-messages="skuError"
                        @keydown="onSkuKeydown"
                      />
                    </v-col>
                    <v-col v-if="showTooltip || isCreate" class="ml-n5" cols="auto">
                      <mf-tooltip variant="secondary" top max-width="250">
                        <template v-slot:activator>
                          <mf-icon color="#ffcc00">report</mf-icon>
                        </template>
                        <span> Produtos sem SKU cadastrado não aparecerão na gestão de ofertas, impossibilitando que sejam criadas ofertas para eles. </span>
                      </mf-tooltip>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col class="mt-n7" cols="5">
                  <search-brands v-if="$getLoggedUser().has_ecommerce" v-model="productForms" />
                </v-col>
                <v-col class="mt-n7" cols="5">
                  <v-text-field
                    v-if="$getLoggedUser().has_ecommerce && !isCreate"
                    v-model="productForms.slug"
                    label="Slug"
                    outlined
                    disabled
                    color="#C3C8CD"
                    height="44px"
                  />
                </v-col>
                <v-col class="mt-n7" cols="5">
                  <h3>EANs</h3>
                  <v-chip-group v-if="!!productForms.eans.length" column show-arrows>
                    <div v-for="(ean, index) in productForms.eans" :key="index">
                      <v-chip
                        v-clipboard="() => ean.ean"
                        color="cinza-l80"
                        :class="[ean.isMain ? 'chip-isMain' : '']"
                        style="cursor: pointer"
                        :ripple="false"
                        @click="$snackbar({ message: 'EAN copiado para a área de transferência.' })"
                      >
                        <span class="column-title d-flex align-center">
                          <b class="">{{ ean.ean }} <mf-icon color="$marca-0-l10" small>file_copy</mf-icon></b>
                        </span>
                      </v-chip>
                      <div v-if="ean.packingQuantity && ean.packingQuantity.quantityPackaging">
                        <span class="small-text-regular-font">Pacote: {{ ean.packingQuantity.quantityPackaging }}</span>
                      </div>
                    </div>
                  </v-chip-group>
                  <div v-else>
                    <mf-button label="Adicionar EAN" icon="add" text color="marca-0-l40" :disabled="!userHasPermission" @click="addEans" />
                  </div>
                  <div v-if="eanError" class="pt-1 error--text v-messages">
                    <span class="v-messages__message">Adicione pelo menos 1 ean</span>
                  </div>
                  <mf-button v-if="productForms.eans.length" color="marca-0-l40" text label="Configurar eans" :disabled="!userHasPermission" @click="addEans" />
                  <search-categories v-model="productForms" :validation="validation" />
                </v-col>
                <v-col v-if="showDatails && !isProductLoading" class="mt-n7" cols="6">
                  <h3 class="mb-5">
                    Dados do Produto
                    <mf-tooltip variant="secondary" top max-width="250">
                      <template v-slot:activator>
                        <mf-icon size="20" class="mb-1">help</mf-icon>
                      </template>
                      <span> Dados apresentados apenas para usuários internos! Estes dados são do catálago Mercafacil. </span>
                    </mf-tooltip>
                  </h3>
                  <v-row>
                    <v-col class="mt-n7" cols="12">
                      <ul v-if="productForms.departmentMf">
                        <li>
                          <b>{{ `Departamento: ${productForms.departmentMf.name}` }}</b>
                          <ul v-if="productForms.categoryMf">
                            <li>
                              <b>{{ `Categoria: ${productForms.categoryMf.name}` }}</b>
                              <ul v-if="productForms.subcategoryMf">
                                <li>
                                  <b>{{ `Subcategoria: ${productForms.subcategoryMf.name}` }}</b>
                                </li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                      </ul>
                      <ul v-if="listInfoProduct" class="mt-n2">
                        <li v-for="item in listInfoProduct" :key="item.value">
                          <b>{{ `${item.name}: ${item.value}` }}</b>
                        </li>
                      </ul>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="!$getLoggedUser().has_ecommerce">
        <v-col cols="3"></v-col>
        <v-col cols="6">
          <v-divider></v-divider>
          <mf-toggle v-model="productForms.isWeightable" class="pb-n10 d-none" label="Produto vendido por medida" hide-details />
          <p class="pt-3 texto-corrido d-none">Configure a medida e o peso base do produto pesável.</p>
          <v-row>
            <v-col cols="4">
              <h3>Medida</h3>
              <v-select
                v-model="productForms.measure"
                :items="measures"
                outlined
                color="#C3C8CD"
                height="44px"
                item-text="name"
                return-object
                :disabled="!userHasPermission"
                :error="validation.product.measureMfId && validation.product.measureMfId.$error"
                :error-messages="measureError"
              />
            </v-col>
            <v-col v-if="productForms.measure && productForms.measure.fractionalSale" cols="8">
              <h3>Peso base</h3>
              <v-text-field
                v-model="productForms.initialWeight"
                outlined
                color="#C3C8CD"
                height="44px"
                type="number"
                min="0"
                :disabled="!productForms.isWeightable && !userHasPermission"
                :error="validation.product.initialWeight && validation.product.initialWeight.$error"
                :error-messages="initialWeightError"
                @keydown="$event.key === '-' || $event.key === 'e' ? $event.preventDefault() : null"
              >
                <template slot="append-outer">
                  <div v-if="productForms.measure.name && productForms.measure.name.length" class="py-1 px-2 append-item">
                    <span class="append-item-text">{{ productForms.measure.acronym }}</span>
                  </div>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-divider />
      <v-row v-if="$getLoggedUser().has_ecommerce">
        <v-col cols="10">
          <h3>Descrição / Especificações</h3>
          <p class="texto-corrido">
            Preencha este campo com a descrição ou as especificações do produto, que é visualizada na página do produto no e-commerce.
          </p>
          <v-textarea
            v-model="productForms.description"
            outlined
            name="input-7-4"
            counter
            :error="validation.product.description && validation.product.description.$error"
            :error-messages="descriptionError"
            :disabled="!userHasPermission"
          >
            <template v-slot:counter="{ props }">
              <pre>{{ props }}</pre>
            </template>
          </v-textarea>
        </v-col>
        <v-col v-if="productForms.isMedicine" cols="10">
          <v-row>
            <v-col class="pt-0" cols="4">
              <h3>Princípio ativo</h3>
              <v-text-field v-model="productForms.activePrinciple" class="pt-2" label="Princípio ativo" outlined height="44px" />
            </v-col>
          </v-row>
        </v-col>
        <v-col v-if="productForms.isMedicine" cols="12">
          <h3>Bula</h3>
          <p class="texto-corrido">Insira aqui dados informativos acerca da composição, dosagem, contraindicações e indicações do medicamento.</p>
          <v-row>
            <v-col class="pt-0" cols="6">
              <v-textarea v-model="productForms.leaflet" outlined counter name="input-7-4" label="Bula...">
                <template v-slot:counter="{ counter }">
                  <h3>Caracteres:</h3>
                  <pre>{{ counter }}</pre>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-col v-if="!productForms.isMedicine" cols="5">
              <h3>Tabela nutricional</h3>
              <p class="texto-corrido">Insira aqui a tabela nutricional do seu produto, que será visualizada na página do produto no e-commerce.</p>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="productForms.nutritionalTable"
                    outlined
                    counter
                    name="input-7-4"
                    :error="validation.product.nutritionalTable && validation.product.nutritionalTable.$error"
                    :error-messages="nutricionalTableError"
                    :disabled="!userHasPermission"
                  >
                    <template v-slot:counter="{ counter }">
                      <h3>Caracteres:</h3>
                      <pre>{{ counter }}</pre>
                    </template>
                  </v-textarea>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <h3>Sinônimos</h3>
              <p class="texto-corrido">
                Após inserir o sinônimo aperte enter ou tab. Caso o consumidor procure por um sinônimo, o e-commerce poderá exibir este produto.
              </p>
              <v-row>
                <v-col cols="12">
                  <div class="custom-combobox">
                    <v-textarea
                      v-model="textareaValue"
                      placeholder="Digite um Sinônimo"
                      hint="Aperte ENTER ou TAB para ir adicionando os sinônimos"
                      outlined
                      class="textarea-with-chips"
                      @keydown.tab.prevent="createChip"
                      @keyup.enter.prevent="createChip"
                    >
                      <template v-slot:prepend-inner>
                        <div class="chips">
                          <v-chip
                            v-for="(synonym, index) in productForms.synonyms"
                            :key="synonym.name"
                            class="chip-in-textarea"
                            close
                            @click:close="removeChip(index)"
                          >
                            {{ synonym.name }}
                          </v-chip>
                        </div>
                      </template>
                    </v-textarea>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <product-eans-table v-model="productForms" :openDialog.sync="openDialog" />
  </v-card>
</template>

<script>
import { MUTATION_UPLOAD_IMAGE } from '../../../graphql'
import SearchBrands from './SearchBrands.vue'
import SearchCategories from './SearchCategories.vue'
import ProductEansTable from '../../components/ProductEansTable.vue'

export default {
  name: 'ProductInfoCard',
  inject: ['userHasPermission'],
  components: {
    SearchBrands,
    SearchCategories,
    ProductEansTable,
  },
  props: {
    loadingImage: {
      type: Boolean,
      default: false,
    },
    isProductLoading: {
      type: Boolean,
      default: false,
    },
    isCreate: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      required: true,
    },
    validation: {
      type: Object,
      required: true,
    },
    measures: {
      type: Array,
      required: true,
    },
    skuValidate: {
      type: String,
      default: '',
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { showAllEans: false, openDialog: false, localImage: '', selectedMeasure: {}, product: { legacySku: '' }, textareaValue: '', chips: [] }
  },
  computed: {
    showDatails() {
      const domain = /@(.+?)\./.exec(this.$getLoggedUser().username)
      const userDomain = domain ? domain[1] : null
      return userDomain === 'mercafacil' && !this.isCreate && (this.productForms.departmentMf || Object.keys(this.listInfoProduct).length > 0)
    },
    listInfoProduct() {
      const infos = [
        { value: 'brandMf', name: 'Marca' },
        { value: 'productMfname', name: 'Modelo' },
        { value: 'productMfId', name: 'Id do catálogo' },
      ]
      const infoData = {}
      infos.forEach((element) => {
        if (this.productForms[element.value] !== null && this.productForms[element.value] !== undefined) {
          const infoValue = this.productForms[element.value]?.name || this.productForms[element.value]
          infoData[element.value] = {
            value: infoValue,
            name: element.name,
          }
        }
      })
      return infoData
    },
    disableSkuInput() {
      return !(this.skuValidate === undefined || this.skuValidate === null || this.skuValidate === '')
    },
    isLoading: {
      get() {
        return this.loadingImage
      },
      set(value) {
        this.$emit('update:loadingImage', value)
      },
    },
    productForms: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    nameError() {
      if (this.validation.product.name && this.validation.product.name.$error) {
        if (!this.validation.product.name.maxLength) return 'Deve conter no máximo 250 caracteres'
        return 'Campo obrigatório'
      }
      return ''
    },
    eanError() {
      return this.validation.product.eans && this.validation.product.eans.$error
    },
    measureError() {
      if (this.validation.product.measure && this.validation.product.measure.name.$error) return 'Campo obrigatório'
      return ''
    },
    initialWeightError() {
      if (this.validation.product.initialWeight && this.validation.product.initialWeight.$error) return 'Campo obrigatório'
      return ''
    },
    skuError() {
      if (this.validation.product.legacySku && this.validation.product.legacySku.$error) {
        if (!this.validation.product.legacySku.maxLength) return 'Deve ter menos de 100 caracteres'
        return 'Campo obrigatório'
      }
      return ''
    },
    additionalWeightError() {
      if (this.validation.product.additionWeight && this.validation.product.additionWeight.$error) return 'Campo obrigatório'
      return ''
    },
    descriptionError() {
      if (this.validation.product.description && this.validation.product.description.$error) return 'Deve conter no máximo 5000 caracteres'
      return ''
    },
    nutricionalTableError() {
      if (this.validation.product.nutritionalTable && this.validation.product.nutritionalTable.$error) return 'Deve conter no máximo 5000 caracteres'
      return ''
    },
  },
  watch: {
    selectedMeasure() {
      if (this.selectedMeasure.hasOwnProperty('id')) {
        const { id, __typename, ...measure } = this.selectedMeasure
        this.productForms.measure = measure
      }
    },
    'value.images': function () {
      if (!this.localImage.length) {
        if (this.productForms.images.length && this.productForms.images[0].url) {
          this.localImage = this.productForms.images[0].url

          return
        }
        this.localImage = this.productForms.images[0].urlThumbnail
      }
    },
    localImage(value) {
      if (!value) {
        this.productForms.images[0].url = ''
        this.productForms.images[0].urlThumbnail = ''
        this.productForms.images[0].urlMiniature = ''
      }
    },
  },
  mounted() {
    this.product = { ...this.value }
  },
  methods: {
    createChip() {
      const value = this.textareaValue.trim()

      if (value !== '' && !this.productForms.synonyms.find((item) => item?.name?.toUpperCase() === value?.toUpperCase())) {
        this.productForms.synonyms.push({
          ecommerceId: this.product.ecommerceId,
          name: value,
        })
      }
      this.textareaValue = ''
    },
    removeChip(index) {
      this.productForms.synonyms.splice(index, 1)
    },
    validateNameinObject(obj) {
      return obj?.name || ''
    },
    onSkuKeydown(event) {
      if (['-', 'e', '.'].includes(event.key)) {
        event.preventDefault()
      }
    },
    saveImage({ file }) {
      this.isLoading = true
      this.$apollo
        .mutate({
          mutation: MUTATION_UPLOAD_IMAGE,
          variables: { file: file },
          context: {
            uri: this.$microservicesUrls['products'],
            headers: {
              'x-apollo-operation-name': 'UploadFile',
            },
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => {
          this.$snackbar({ message: 'Imagem salva com sucesso.' })
          this.productForms.images[0].url = data.uploadFile.url
          this.productForms.images[0].urlThumbnail = data.uploadFile.urlThumb
          this.productForms.images[0].urlMiniature = data.uploadFile.urlMini
        })
        .catch(() => this.$snackbar({ message: 'Erro ao salvar imagem.', snackbarColor: 'error' }))
        .finally(() => (this.isLoading = false))
    },
    addEans() {
      this.openDialog = true
    },
    showSnackbarError(snackbar) {
      this.$snackbar(snackbar)
    },
  },
}
</script>

<style lang="scss">
.card-title-information {
  color: $cinza-d10 !important;
  font-weight: 700 !important;
}
.custom-combobox {
  .v-input > .v-input__control > .v-input__slot > .v-text-field__slot {
    width: 50% !important;
    border-left: 1px solid #606060;
  }
}
.textarea-with-chips {
  overflow-y: auto;
  resize: none;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  max-height: 130px;
  width: 500px;
}

.chip-in-textarea {
  margin-right: 4px;
  margin-bottom: 4px;
}
.card-information {
  .v-text-field--filled > .v-input__control > .v-input__slot,
  .v-text-field--full-width > .v-input__control > .v-input__slot,
  .v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: 0 !important;
  }

  .v-text-field--outlined .v-label {
    top: 12px;
  }

  .v-label--active {
    top: 18px !important;
    color: $marca-0 !important;
  }

  .v-text-field--enclosed .v-input__append-inner,
  .v-text-field--enclosed .v-input__append-outer,
  .v-text-field--enclosed .v-input__prepend-inner,
  .v-text-field--enclosed .v-input__prepend-outer,
  .v-text-field--full-width .v-input__append-inner,
  .v-text-field--full-width .v-input__append-outer,
  .v-text-field--full-width .v-input__prepend-inner,
  .v-text-field--full-width .v-input__prepend-outer {
    margin-top: 11px;
  }

  .v-textarea.v-text-field--enclosed.v-text-field--outlined:not(.v-input--dense) textarea {
    margin-top: 0;
  }

  .v-chip--clickable {
    cursor: inherit;
  }

  .chip-isMain {
    border: 2px solid $marca-0-l30 !important;
    color: $marca-0-l10 !important;
  }

  .button-position {
    transform: translateX(-1rem);
  }

  .column-title:first-child {
    color: $marca-0-l10;
  }

  .mf-container {
    width: 100%;
  }
}
</style>
